import {
	PortfolioBuyAveragePriceCalculationType,
	PortfolioCalculationType,
	PortfolioTotalValueCalculateType
} from '@easy-lib/modules/settings';
import { NormalPercentPipe } from '@easy-lib/pipes/normalPercent.pipe';
import { SortStates } from '@easy-lib/tools/sort';
import { PriceNumberPipe } from '../../../pipes';
import { IPortfolioItem } from '@easy-lib/modules/portfolio/models/portfolio-item.model';

export interface IPortfolioModel {
	performanceResponse: IPerformanceResponse;
	omsPortfolio: IOmsPortfolio;
}

export interface IPortfolioSettings {
	totalCalculationType: PortfolioTotalValueCalculateType;
	calculationType: PortfolioCalculationType;
	portfolioBuyAveragePriceCalculationType: PortfolioBuyAveragePriceCalculationType;
}
export interface IPerformanceResponse {
	items: IPortfolioItem[];
}
export interface IPerformanceAssetValueHistoryResponse {
	items: IPortfolioAssetValueHistory[];
}

export const portfolioItemDefaultValue: IPortfolioItem = {
	symbolIsin: '',
	symbolName: '',
	date: '',
	persianDate: 0,
	asset: 0,
	bonusShareVolume: 0,
	stockRightVolume: 0,
	periodicDPSAmount: 0,
	lastDPSAmount: 0,
	totalDPSAmount: 0,
	breakevenPoint: 0,
	breakevenWithoutDps: 0,
	periodicBuyAveragePrice: 0,
	periodicBuyAveragePriceWithoutDps: 0,
	totalBuyAveragePrice: 0,
	totalSellAveragePrice: 0,
	totalBuyAveragePriceWithoutDps: 0,
	periodicBuyAmount: 0,
	totalBuyAmount: 0,
	periodicSellAveragePrice: 0,
	periodicSellAmount: 0,
	totalSellAmount: 0,
	periodicBoughtVolume: 0,
	totalBoughtVolume: 0,
	periodicSoldVolume: 0,
	totalSoldVolume: 0,
	periodicRealizedProfitAndLoss: 0,
	periodicRealizedProfitAndLossWithoutDps: 0,
	totalRealizedProfitAndLoss: 0,
	totalRealizedProfitAndLossWithoutDps: 0,
	calculationVersion: '',
	dailyBuyAveragePrice: 0,
	dailySellAveragePrice: 0,
	dailyBoughtVolume: 0,
	dailySoldVolume: 0
};

export interface IPortfolioItemsCalculationTotalFields {
	totalAssetsValue: number;
	totalBonusValue: number;
	totalPortfolioValue: number;
	totalProfitAmount: number;
	totalProfitPercent: number;
	totalLastDPSAmount: number;
	totalSoldProfit: number;
	totalTodayProfitAmount: number;
	totalTodayProfitPercent: number;
	totalAssetCount: number;
}

export interface ISoldItemsCalculationTotalFields {
	totalLastDPSAmount: number;
	totalSoldProfit: number;
}

export interface IPortfolioAssetValueHistory {
	date: string;
	dateNumbered: number;
	assetValue: number;
	buyOrSellValue: number;
}
export interface IPortfolioTransactionsHistoryResponse {
	items: any[];
	symbolIsin: string;
	symbolName: string;
}
export interface IPortfolioTransactionsHistory {
	date: Date;
	persianDate: number;
	time: string;
	calcedDateTime?: string;
	asset: number;
	type: PortfolioItemDetailTransactionTypes;
	rowType: PortfolioItemDetailTransactionTypes;
	price: number;
	volume: number;
	periodicAveragePrice: number;
	periodicVolume: number;
	totalAveragePrice: number;
	totalVolume: number;
	amount: number;
	periodicAveragePriceWithoutDps: number;
	totalAveragePriceWithoutDps: number;
	realizedProfitAndLoss: number;
	realizedProfitAndLossWithoutDps: number;
	corporateActionValue: number;
	corporateActionId: any;
	calculationVersion: string;
	averagePrice?: number;
}

export interface IPortfolioSoldItemsResponse {
	items: IPortfolioItem[];
}

export interface IPortfolioSoldItems extends IPortfolioItem {}
export interface IPortfolioMarketData {
	symbolIsin: string;
	stateCode?: string;
	closingPrice?: number;
	lastTradedPrice?: number;
	feeOfPreviousDaysClosingPrice?: number;
	totalNumberOfSharesTraded?: number;
	lastTradedPricePercent?: number;
	lsUpdatedTimestamp: number;
}

export interface IOmsPortfolio {
	portfolioItems: {
		symbolName: string;
		meanBuyTradePrice: number;
		meanSellTradePrice: number;
		meanBuyOrderPrice: number;
		meanSellOrderPrice: number;
		isin: string;
		asset: number;
		openBuyQuantity: number;
		openSellQuantity: number;
		tradedBuyQuantity: number;
		tradedSellQuantity: number;
	}[];
}

export enum PortfolioItemDetailTransactionTypes {
	Join = 'Join', //1, //تغییر کارگزار ناظر ورود
	Leave = 'Leave', //2, // تغییر کارگزار ناظر خروج
	Buy = 'Buy', //3, // خرید
	Sell = 'Sell', //4, // فروش
	DividendShare = 'DividendShare', //5, //سود مجمع
	BonusShare = 'BonusShare', //6, //افزایش سرمایه(سهام جایزه)
	ChangeBonusShareToAsset = 'ChangeBonusShareToAsset', //7, //تبدیل سهام جایزه به سهم
	UnknownIncreaseAsset = 'UnknownIncreaseAsset', //8, //خرید ناشناس
	UnknownDecreaseAsset = 'UnknownDecreaseAsset', //9, //فروش ناشناس
	StockRight = 'StockRight', //10, //افزایش سرمایه(حق تقدم)
	ChangeStockRightToAsset = 'ChangeStockRightToAsset' //11 //تبدیل حق تقدم به سهم
}

export const PortfolioItemDetailTransactionCanUseWithoutDps: Record<string, boolean> = {
	[PortfolioItemDetailTransactionTypes.Join]: true,
	[PortfolioItemDetailTransactionTypes.Leave]: false,
	[PortfolioItemDetailTransactionTypes.Buy]: true,
	[PortfolioItemDetailTransactionTypes.Sell]: false,
	[PortfolioItemDetailTransactionTypes.DividendShare]: true,
	[PortfolioItemDetailTransactionTypes.BonusShare]: true,
	[PortfolioItemDetailTransactionTypes.ChangeBonusShareToAsset]: true,
	[PortfolioItemDetailTransactionTypes.UnknownIncreaseAsset]: true,
	[PortfolioItemDetailTransactionTypes.UnknownDecreaseAsset]: false,
	[PortfolioItemDetailTransactionTypes.StockRight]: true,
	[PortfolioItemDetailTransactionTypes.ChangeStockRightToAsset]: true
};

export const portfolioTransactionItemTypeTemplate: Record<
	string,
	{ lable: string; fieldName: string; value: (val) => string; valuePostFix: string; needColorize?: boolean }[]
> = {
	[PortfolioItemDetailTransactionTypes.Sell]: [
		{
			lable: 'نوع تراکنش',
			fieldName: 'rowType',
			value: title => title,
			valuePostFix: ''
		},
		{
			lable: 'زمان',
			fieldName: 'calcedDateTime',
			value: val => {
				return val;
			},
			valuePostFix: ''
		},
		{
			lable: 'تعداد',
			fieldName: 'volume',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت',
			fieldName: 'price',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'ارزش معامله',
			fieldName: 'amount',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'مانده سهم پس از تراکنش',
			fieldName: 'asset',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت میانگین',
			fieldName: 'averagePrice',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'سود/ زیان فروخته شده',
			fieldName: 'realizedProfitAndLoss',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء',
			needColorize: true
		},
		{
			lable: 'سود/ زیان فروخته شده',
			fieldName: 'realizedProfitAndLossWithoutDps',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء',
			needColorize: true
		}
	],
	[PortfolioItemDetailTransactionTypes.Buy]: [
		{
			lable: 'نوع تراکنش',
			fieldName: 'rowType',
			value: title => title,
			valuePostFix: ''
		},
		{
			lable: 'زمان',
			fieldName: 'calcedDateTime',
			value: value => {
				return value;
			},
			valuePostFix: ''
		},
		{
			lable: 'تعداد',
			fieldName: 'volume',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت',
			fieldName: 'price',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'ارزش معامله',
			fieldName: 'amount',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'مانده سهم پس از تراکنش',
			fieldName: 'asset',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت میانگین',
			fieldName: 'averagePrice',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		}
	],
	[PortfolioItemDetailTransactionTypes.BonusShare]: [
		{
			lable: 'نوع تراکنش',
			fieldName: 'rowType',
			value: title => title,
			valuePostFix: ''
		},
		{
			lable: 'زمان',
			fieldName: 'calcedDateTime',
			value: value => {
				return value;
			},
			valuePostFix: ''
		},
		{
			lable: 'تعداد',
			fieldName: 'volume',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت',
			fieldName: 'price',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'ارزش معامله',
			fieldName: 'amount',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'مانده سهم پس از تراکنش',
			fieldName: 'asset',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت میانگین',
			fieldName: 'averagePrice',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'درصد افزایش سرمایه',
			fieldName: 'corporateActionValue',
			value: value => {
				return new NormalPercentPipe().transform(value * 100) + '%';
			},
			valuePostFix: ''
		}
	],
	[PortfolioItemDetailTransactionTypes.DividendShare]: [
		{
			lable: 'نوع تراکنش',
			fieldName: 'rowType',
			value: title => title,
			valuePostFix: ''
		},
		{
			lable: 'زمان',
			fieldName: 'calcedDateTime',
			value: value => {
				return value;
			},
			valuePostFix: ''
		},
		{
			lable: 'ارزش سود نقدی',
			fieldName: 'price',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'مانده سهم پس از تراکنش',
			fieldName: 'asset',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'سهم'
		},
		{
			lable: 'قیمت میانگین',
			fieldName: 'averagePrice',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		},
		{
			lable: 'مقدار سود نقدی',
			fieldName: 'corporateActionValue',
			value: value => {
				return new PriceNumberPipe().transform(value);
			},
			valuePostFix: 'ریالءء'
		}
	]
};

export const PortfolioItemDetailTransactionTypeInfo: Record<
	string,
	{
		title: string;
		class: string;
		bgColor: string;
		template: {
			lable: string;
			fieldName: string;
			value: (val) => string;
			valuePostFix: string;
			needColorize?: boolean;
		}[];
	}
> = {
	//#region خرید ها
	[PortfolioItemDetailTransactionTypes.Buy]: {
		title: 'خرید',
		class: 'text-success',
		bgColor: 'bg-success',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Buy]
	},
	[PortfolioItemDetailTransactionTypes.UnknownIncreaseAsset]: {
		title: 'خرید ناشناس',
		class: 'text-success',
		bgColor: 'bg-success',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Buy]
	},
	[PortfolioItemDetailTransactionTypes.Join]: {
		title: 'انتقال نماد به کارگزاری مفید',
		class: 'text-success',
		bgColor: 'bg-success',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Buy]
	},
	//#endregion  خرید ها
	//#region فروش ها
	[PortfolioItemDetailTransactionTypes.Sell]: {
		title: 'فروش',
		class: 'text-danger',
		bgColor: 'bg-danger',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Sell]
	},
	[PortfolioItemDetailTransactionTypes.UnknownDecreaseAsset]: {
		title: 'فروش ناشناس',
		class: 'text-danger',
		bgColor: 'bg-danger',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Sell]
	},
	[PortfolioItemDetailTransactionTypes.Leave]: {
		title: 'خروج نماد از کارگزاری مفید',
		class: 'text-danger',
		bgColor: 'bg-danger',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.Sell]
	},
	//#endregion فروش ها
	//#region افزایش سرمایه
	[PortfolioItemDetailTransactionTypes.BonusShare]: {
		title: 'افزایش سرمایه (سهام جایزه)',
		class: 'text-secondary',
		bgColor: 'bg-secondary',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.BonusShare]
	},
	[PortfolioItemDetailTransactionTypes.ChangeBonusShareToAsset]: {
		title: 'تبدیل سهام جایزه به سهم',
		class: 'text-secondary',
		bgColor: 'bg-secondary',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.BonusShare]
	},
	[PortfolioItemDetailTransactionTypes.StockRight]: {
		title: 'افزایش سرمایه(حق تقدم)',
		class: 'text-secondary',
		bgColor: 'bg-secondary',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.BonusShare]
	},
	[PortfolioItemDetailTransactionTypes.ChangeStockRightToAsset]: {
		title: 'تبدیل حق تقدم به سهم',
		class: 'text-secondary',
		bgColor: 'bg-secondary',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.BonusShare]
	},
	//#endregion افزایش سرمایه
	[PortfolioItemDetailTransactionTypes.DividendShare]: {
		title: 'سود نقدی مجمع',
		class: 'text-secondary',
		bgColor: 'bg-secondary',
		template: portfolioTransactionItemTypeTemplate[PortfolioItemDetailTransactionTypes.DividendShare]
	}
};

export enum NewPortfolioSortType {
	None = 'None',
	SymbolName = 'symbolName',
	Asset = 'asset',
	CurrentValue = 'currentValue',
	CalculatedBreakEvenPoint = 'calculatedBreakEvenPoint',
	LatestPrice = 'lastTradedPricePercent',
	closePrice = 'closingPricePercent',
	// Percent,
	ProfitAmount = 'profitAmount'
}

export enum AssemblyProfitSortType {
	None = 'None',
	SymbolName = 'symbolName',
	LastDPSAmount = 'lastDPSAmount',
	TotalDPSAmount = 'totalDPSAmount'
}

export enum SoldItemsSortType {
	None = 'None',
	SoldProfit = 'soldProfit',
	SymbolName = 'symbolName',
	Date = 'date'
}

export enum NewPortfolioSortKey {
	None = 'None',
	SymbolNameQuantity = 'SymbolNameQuantity',
	Value = 'Value',
	LastClosePrice = 'LastClosePrice',
	Percent = 'Percent',
	// ValueNetPrice = 'ValueNetPrice',
	ValueCurrentProfit = 'ValueCurrentProfit',
	// lastPriceCurrentProfit = 'lastPriceCurrentProfit'
	lastPriceNetPrice = 'lastPriceNetPrice'
}

export interface NewPortfolioSort {
	type: NewPortfolioSortType;
	key?: NewPortfolioSortKey;
	dir?: SortStates;
}

export const portfolioDetailSymbolSharesPallette: Map<number, string> = new Map<number, string>([
	[0, '#31E8E8'],
	[1, '#FFC168'],
	[2, '#FF8479'],
	[3, '#FF3871'],
	[4, '#C63295'],
	[5, '#8E43E7'],
	[6, '#3369E7'],
	[7, '#00AEFF']
]);
